import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import { cancelSubscription, getCancellationReasons } from "../../../api/services/userService";

const GREEN = "#A9C74E";
const RED = "#F04951";
const DARK = "#20202B";
const MUTED = "#7A7A88";

const formatDateOnly = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

// Honest impact copy: real charges_paid count for this subscription, not a made-up
// patient number (Figma's mockup hardcodes "12 patients" — we don't have that data).
const contributionLine = (chargesPaid) => {
  const n = Number(chargesPaid) || 0;
  if (n <= 0) return "Thank you for starting this monthly commitment.";
  if (n === 1) return "You've made 1 monthly contribution so far.";
  return `You've made ${n} monthly contributions so far.`;
};

/**
 * Cancel Subscription modal — Figma nodes 2696:4752 (form), 2696:4855 (form, reason
 * selected), 2697:4752 ("kept" screen), 2697:4786 ("cancelled" screen).
 *
 * `subscription` is one row from GET /payment/subscription/list/:userId — needs at
 * least: subscriptionId, category_label (or campaignName), status_label, amount,
 * nextBillingDate, charges_paid.
 */
const CancelSubscriptionModal = ({ open, onClose, subscription, onCancelled }) => {
  const [step, setStep] = useState("form"); // form | kept | cancelled
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Reset to a clean form every time the modal is opened for a (possibly different) subscription
  useEffect(() => {
    if (open) {
      setStep("form");
      setReason("");
      setComment("");
      setError("");
    }
  }, [open, subscription?.subscriptionId]);

  useEffect(() => {
    if (!open || reasons.length > 0) return;
    getCancellationReasons()
      .then((res) => setReasons(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setReasons([])); // dropdown still usable empty; cancel just requires picking nothing (backend tolerates missing reason)
  }, [open, reasons.length]);

  if (!subscription) return null;

  const handleKeep = () => setStep("kept");

  const handleCancel = async () => {
    if (!reason) {
      setError("Please select a cancellation reason.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await cancelSubscription(subscription.subscriptionId, reason, comment.trim());
      setStep("cancelled");
      onCancelled?.();
    } catch (err) {
      setError(err?.message || "Failed to cancel subscription. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      {step === "form" && (
        <Box sx={{ p: "32px", display: "flex", flexDirection: "column", gap: "24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "12px",
                  bgcolor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#D97706" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", color: DARK }}>
                Cancel Subscription
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={submitting}
              sx={{ width: 24, height: 24, bgcolor: "#F3F4F6", "&:hover": { bgcolor: "#E5E7EB" } }}
            >
              <CloseIcon sx={{ fontSize: 14, color: DARK }} />
            </IconButton>
          </Box>

          <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: MUTED }}>
            Cancelling your recurring donation will stop all future payments. A cancellation
            confirmation will be sent to your registered email.
          </Typography>

          <Box
            sx={{
              bgcolor: "#F8F9FA",
              border: "1px solid #E9E9E9",
              borderRadius: "12px",
              p: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography sx={{ fontSize: "12px", color: MUTED, fontWeight: 500 }}>Cause</Typography>
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: DARK }}>
                  {subscription.category_label || subscription.campaignName || "Monthly Donation"}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: "#EBF7ED",
                  borderRadius: "100px",
                  px: "10px",
                  py: "4px",
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: GREEN }}>
                  {subscription.status_label || subscription.status}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ borderBottom: "1px solid #E9E9E9" }} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography sx={{ fontSize: "11px", color: MUTED, fontWeight: 500 }}>Amount</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: DARK }}>
                  ₹{subscription.amount}/month
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "11px", color: MUTED, fontWeight: 500 }}>Next Payment</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: DARK }}>
                  {formatDateOnly(subscription.nextBillingDate)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: DARK }}>
              Cancellation Reason *
            </Typography>
            <Select
              value={reason}
              displayEmpty
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError("");
              }}
              sx={{
                height: "44px",
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#BEBFD0" },
                "& .MuiSelect-select": { fontSize: "14px", fontWeight: 600, color: reason ? DARK : "#9CA3AF" },
              }}
              renderValue={(selected) => {
                if (!selected) return "Select reason";
                const match = reasons.find((r) => r.value === selected);
                return match?.label || selected;
              }}
            >
              {reasons.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: DARK }}>
              Additional Comments (Optional)
            </Typography>
            <TextField
              multiline
              minRows={3}
              placeholder="Tell us more about why you are cancelling..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDD" },
              }}
            />
          </Box>

          {error && (
            <Typography sx={{ fontSize: "13px", color: RED, fontWeight: 500, mt: "-8px" }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button
              onClick={handleKeep}
              disabled={submitting}
              sx={{
                flex: 1,
                py: "14px",
                borderRadius: "8px",
                bgcolor: GREEN,
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#98B845" },
              }}
            >
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancel}
              disabled={submitting}
              sx={{
                flex: 1,
                py: "14px",
                borderRadius: "8px",
                border: "1px solid #D9D9DE",
                color: submitting ? MUTED : MUTED,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#F5F5F5" },
              }}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: MUTED }} /> : "Cancel Subscription"}
            </Button>
          </Box>
        </Box>
      )}

      {step === "kept" && (
        <Box sx={{ p: "32px", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px", textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "32px",
              bgcolor: "#EBF7ED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HighlightOffRoundedIcon sx={{ fontSize: 32, color: GREEN }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: DARK }}>
              Thank You for Staying! 💚
            </Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "22px", color: "#5C5C6A" }}>
              Your continued support means the world to families fighting rare diseases and
              critical illnesses. Every month, your contribution brings hope, treatment, and a
              chance at life to those who need it most.
            </Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "22px", fontWeight: 600, color: GREEN }}>
              Together, we're making miracles happen — one heartbeat at a time.
            </Typography>
          </Box>
          <Box sx={{ borderBottom: "1px solid #E9E9E9", width: "100%" }} />
          <Box
            sx={{
              bgcolor: "#EBF7ED",
              borderRadius: "8px",
              p: "12px",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <FavoriteRoundedIcon sx={{ fontSize: 16, color: GREEN }} />
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: GREEN }}>
              {contributionLine(subscription.charges_paid)}
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              py: "14px",
              borderRadius: "8px",
              bgcolor: RED,
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: "#DB3038" },
            }}
          >
            Back to My Subscriptions
          </Button>
          <Typography sx={{ fontSize: "12px", color: MUTED }}>
            Thank you for being part of the TrueHope family.
          </Typography>
        </Box>
      )}

      {step === "cancelled" && (
        <Box sx={{ p: "32px", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px", textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "32px",
              bgcolor: "#FFF5F0",
              border: "1px solid rgba(240,73,81,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HighlightOffRoundedIcon sx={{ fontSize: 28, color: RED }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: DARK }}>
              Subscription Cancelled
            </Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "22px", color: "#5C5C6A" }}>
              We're sorry to see you go. Your support has made a real difference in the lives of
              patients battling rare diseases and critical illnesses.
            </Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "22px", color: "#5C5C6A" }}>
              Even though this chapter is closing, the impact you've made will continue to heal
              and inspire. Thank you for every moment of generosity.
            </Typography>
          </Box>
          <Box sx={{ borderBottom: "1px solid #E9E9E9", width: "100%" }} />
          <Box
            sx={{
              bgcolor: "#FFF8F0",
              borderRadius: "8px",
              p: "12px",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <FavoriteRoundedIcon sx={{ fontSize: 16, color: "#B25E00" }} />
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#B25E00" }}>
              {contributionLine(subscription.charges_paid).replace("You've made", "You made").replace("so far.", "during this subscription.")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                py: "14px",
                borderRadius: "8px",
                border: "1px solid #EAEAF0",
                color: "#5C5C6A",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#F5F5F5" },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
          <Typography sx={{ fontSize: "12px", color: MUTED }}>
            You can reactivate your subscription anytime. We'll be here.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default CancelSubscriptionModal;
